import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import { setAuth } from '../Redux/authSlice';
import { translations } from '../utils/translations';
import axios from 'axios';
import { Modal, Button, Card, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEye, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Sales.css';

interface Product {
    product_id: number;
    product_name: string;
    quantity: number;
    selling_price: number;
}

interface Sale {
    id: number;
    customer: string | null;
    walk_in_customer: string;
    products: Product[];
    total_amount: number;
    amount_paid: number;
    balance: number;
    status: string;
    payment_type: string;
    reference_number: string;
    customer_name: string;
    price_markup: number;
    price_discount: number;
}

const SalesComponent: React.FC = () => {
    const [sales, setSales] = useState<Sale[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'view' | 'edit'>('view');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [userRole, setUserRole] = useState<string>(''); // State for user role

    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    const getCookie = (name: string): string => {
        const cookieName = name + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let cookie of cookieArray) {
            cookie = cookie.trim();
            if (cookie.startsWith(cookieName)) {
                return cookie.substring(cookieName.length);
            }
        }
        return '';
    };

    const fetchData = useCallback(async () => {
        try {
            const token = getCookie('token');
            if (!token) {
                dispatch(setAuth(false));
                return;
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const { data } = await axios.get('https://api.sominnovations.xyz/users/user', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setUserRole(data.user.role); // Set the user role
            dispatch(setAuth(true));
        } catch (e) {
            dispatch(setAuth(false));
        }
    }, [dispatch]);

    const fetchSales = useCallback(async (page: number) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `https://api.sominnovations.xyz/Branches/sales/?page=${page}&customer_name=${searchTerm}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            setSales(response.data.results);
            setTotalPages(Math.ceil(response.data.count / 20));
        } catch (error) {
            console.error('Error fetching sales:', error);
        }
        setLoading(false);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [dispatch, fetchData]);

    useEffect(() => {
        if (auth) {
            fetchSales(currentPage);
        }
    }, [auth, currentPage, searchTerm, fetchSales]);

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this sale?')) {
            try {
                await axios.delete(`https://api.sominnovations.xyz/Branches/sales/${id}/`);
                setSales(sales.filter((sale) => sale.id !== id));
                alert('Sale deleted successfully.');
            } catch (error) {
                console.error('Error deleting sale:', error);
                alert('Failed to delete sale. Please try again.');
            }
        }
    };

    const handleView = (sale: Sale) => {
        setSelectedSale(sale);
        setModalType('view');
        setShowModal(true);
    };

    const handleEdit = (sale: Sale) => {
        setSelectedSale(sale);
        setModalType('edit');
        setShowModal(true);
    };

    const handleSaveEdit = async () => {
        if (selectedSale) {
            try {
                await axios.put(
                    `https://api.sominnovations.xyz/Branches/sales/${selectedSale.id}/`,
                    selectedSale
                );
                setSales(sales.map((s) => (s.id === selectedSale.id ? { ...s, ...selectedSale } : s)));
                setShowModal(false);
                alert('Sale updated successfully.');
            } catch (error) {
                console.error('Error updating sale:', error);
                alert('Failed to update sale. Please try again.');
            }
        }
    };

    return (
        <div className="container sales-product" style={{ padding: '20px' }}>
            <Card className="mb-3 shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                    <h5 className="mb-0">{t.sales}</h5>
                </Card.Header>
                <Card.Body>
                    <input
                        type="text"
                        placeholder="Search sales by customer_name..."
                        className="form-control mb-3"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {loading ? (
                        <div className="d-flex justify-content-center my-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Reference</th>
                                        <th>{t.customers}</th>
                                        <th>{t.total}</th>
                                        <th>Status</th>
                                        <th>{t.action}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map((sale) => (
                                        <tr key={sale.id}>
                                            <td>{sale.reference_number}</td>
                                            <td>{sale.customer_name || sale.walk_in_customer}</td>
                                            <td>Ksh {sale.total_amount?.toLocaleString() || 0}</td>
                                            <td>
                                                <span className={`badge ${sale.status === 'Completed' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                    {sale.status}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-outline-primary btn-sm me-2"
                                                    onClick={() => handleView(sale)}
                                                    title="View"
                                                >
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary btn-sm me-2"
                                                    onClick={() => handleEdit(sale)}
                                                    title="Edit"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                {/* Conditionally render the delete button */}
                                                {userRole === 'admin' && (
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        onClick={() => handleDelete(sale.id)}
                                                        title="Delete"
                                                    >
                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
                <Card.Footer className="bg-white border-top-0">
                    <Pagination className="justify-content-center mb-0">
                        {[...Array(totalPages)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </Card.Footer>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalType === 'view' ? 'View Sale' : 'Edit Sale'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalType === 'view' ? (
                        <div>
                            <p>Reference Number: {selectedSale?.reference_number}</p>
                            <p>Customer: {selectedSale?.customer_name || selectedSale?.walk_in_customer}</p>
                            <p>Total Amount: {selectedSale?.total_amount}</p>
                            <p>Status: {selectedSale?.status}</p>
                            <p>Balance: {selectedSale?.balance}</p>
                            <p>Payment type: {selectedSale?.payment_type}</p>
                            <p>Price Markup: {selectedSale?.price_markup}</p>
                            <p>Discount: {selectedSale?.price_discount}</p>
                            <h5>Products:</h5>
                            {selectedSale?.products.map((product) => (
                                <div key={product.product_id} className="mb-3">
                                    <p>Product Name: {product.product_name}</p>
                                    <p>Quantity: {product.quantity}</p>
                                    <p>Selling Price: {product.selling_price}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <form>
                            <div className="mb-3">
                                <label>Customer</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedSale?.customer_name || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, customer: e.target.value } : prev
                                        )
                                    }
                                />
                            </div>
                            <div className="mb-3">
                                <label>Total Amount</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={selectedSale?.total_amount || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, total_amount: parseFloat(e.target.value) } : prev
                                        )
                                    }
                                />
                            </div>
                            <div className="mb-3">
                                <label>Amount Paid</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={selectedSale?.amount_paid || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, amount_paid: parseFloat(e.target.value) } : prev
                                        )
                                    }
                                />
                            </div>
                            <div className="mb-3">
                                <label>Balance</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={selectedSale?.balance || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, balance: parseFloat(e.target.value) } : prev
                                        )
                                    }
                                />
                            </div>
                            <div className="mb-3">
                                <label>Payment Type</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedSale?.payment_type || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, payment_type: e.target.value } : prev
                                        )
                                    }
                                />
                            </div>
                            <div className="mb-3">
                                <label>Status</label>
                                <select
                                    className="form-control"
                                    value={selectedSale?.status || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, status: e.target.value } : prev
                                        )
                                    }
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Completed">Completed</option>
                                </select>
                            </div>
                            <div className="mb-3">
                                <label>Reference Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={selectedSale?.reference_number || ''}
                                    onChange={(e) =>
                                        setSelectedSale((prev) =>
                                            prev ? { ...prev, reference_number: e.target.value } : prev
                                        )
                                    }
                                />
                            </div>
                        </form>
                    )}
                </Modal.Body>
                {modalType === 'edit' && (
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleSaveEdit}>
                            Save Changes
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
        </div>
    );
};

export default SalesComponent;